import { z } from 'zod';
import { anonymityModeSchema, questionTypeSchema, surveyDefinitionSchema, surveyStatusSchema, } from './survey.js';
export const roles = ['consultancy_admin', 'vendor_admin'];
export const roleSchema = z.enum(roles);
export const emailSchema = z.string().trim().toLowerCase().email().max(320);
// --- auth ---
export const requestMagicLinkSchema = z.object({
    email: emailSchema,
    /** Where to send the user after verification; validated server-side as a same-origin path. */
    redirectTo: z.string().max(500).optional(),
});
/**
 * Redeems an emailed magic link. The token is redeemed by this POST rather than by the link's
 * GET, so mail security scanners that pre-fetch the URL cannot spend the single-use token before
 * the recipient clicks.
 */
export const verifyMagicLinkSchema = z.object({
    token: z.string().min(1, 'a sign-in token is required').max(1000),
});
/** Username/password sign-in for the consultancy admin portal only. */
export const adminLoginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'a password is required').max(200),
});
// --- contact ---
export const contactMessageSchema = z.object({
    name: z.string().trim().min(1, 'your name is required').max(120),
    email: emailSchema,
    subject: z.string().trim().min(1, 'a subject is required').max(200),
    message: z.string().trim().min(1, 'a message is required').max(5000),
});
// --- surveys (admin) ---
export const createSurveySchema = z.object({
    vendorId: z.string().uuid(),
    slug: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9-]+$/, 'slug may only contain lowercase letters, numbers and hyphens'),
    anonymityMode: anonymityModeSchema,
    definition: surveyDefinitionSchema,
});
export const updateSurveySchema = z.object({
    vendorId: createSurveySchema.shape.vendorId.optional(),
    slug: createSurveySchema.shape.slug.optional(),
    anonymityMode: anonymityModeSchema.optional(),
    definition: surveyDefinitionSchema.optional(),
});
export const submitSurveySchema = z.object({
    /** Keyed by question id; values are per-type envelopes validated against the definition. */
    answers: z.record(z.string(), z.unknown()),
    /** Only meaningful when the survey is in `opt_in` mode; ignored otherwise. */
    consentToIdentify: z.boolean().optional(),
});
// --- roster ---
export const uploadRosterSchema = z.object({
    emails: z.array(emailSchema).min(1).max(10000),
    /** When true, roster entries absent from this list are removed. */
    replaceExisting: z.boolean().default(false),
});
/**
 * A company logo supplied as an inline `data:` URL. Capped well under the 1 MB request-body limit;
 * the admin UI downscales before upload, so real logos land far below this. Restricted to image
 * media types — the value is only ever rendered inside an `<img>`, never executed.
 */
export const logoDataUrlSchema = z
    .string()
    .max(800_000, 'that image is too large — please use one under about 500 KB')
    .regex(/^data:image\/(png|jpeg|jpg|gif|webp|svg\+xml);base64,[A-Za-z0-9+/=]+$/, 'not an image file');
export const updateVendorSchema = z.object({
    /** Pass a data URL to set the logo, or null to clear it. */
    logoUrl: logoDataUrlSchema.nullable(),
});
export const inviteAdminSchema = z.object({
    email: emailSchema,
    role: roleSchema,
    /** Required for vendor_admin, must be null for consultancy_admin. */
    vendorId: z.string().uuid().nullable(),
});
//# sourceMappingURL=dto.js.map